import React from 'react';
import { NavItem } from '~/components';
import { useTranslation, useAppDispatch } from '~/behaviors';
import { accountActions } from '~/behaviors/actions/account.actions';
import { mui } from '~/tools/mui';
import { icons } from '~/tools/icons';
import { jss } from '~/tools/jss';
import { propTypes } from '~/tools/prop_types';
import { location } from '~/adapters';
import { Link, useNavigate } from 'react-router-dom';

const useStyles = jss.makeStyles((theme) => {
  return {
    header: {
      // @ts-ignore @TODO Epic WT-21600
      height: theme.layout.headerHeight,
      width: '100%',
      textAlign: 'center',
      // @ts-ignore @TODO Epic WT-21600
      backgroundColor: theme.palette.brandBlue,
    },
    logoContainer: {
      height: '100%',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    logo: {
      height: 36,
      width: 148,
    },
    linksNav: {
      paddingTop: 27,
      paddingLeft: 16,
    },
    list: {
      paddingTop: 0,
      paddingBottom: 0,
    },
    extraNav: {
      paddingTop: 60,
      paddingLeft: 32,
    },
    extraListItem: {
      lineHeight: '15px',
      padding: 0,
      '&:not(:last-child)': {
        marginBottom: '8px',
      },
    },
    extraListItemText: {
      // @ts-ignore @TODO Epic WT-21600
      color: theme.palette.gray3,
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '10px',
      textTransform: 'uppercase',
      textDecoration: 'none',
    },
    backArrow: {
      float: 'left',
      filter: 'invert(53%) sepia(1%) saturate(0%) hue-rotate(134deg) brightness(95%) contrast(92%)',
      marginTop: '10px',
    },
    divider: {
      border: '1px solid #dbdcdb',
    },
    pulseHeader: {
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
      padding: '0 0 0 16px',
      textTransform: 'uppercase',
      fontWeight: 'bold',
      color: '#757575',
    },
  };
});

/**
 * Sidebar (blue bar on left of main section).
 *
 * Only shows links that the currently logged-in user has permission to see.
 */
const Sidebar = ({
  showLeaveSurveysAndInsights = false,
  showSurveys = false,
  showConversations = false,
  showActionPlanning = false,
  showEmployees = false,
  showUsers = false,
  showMailingLists = false,
  showSettings = false,
  showAdmin = false,
  showTrends = false,
  showLearningCoach = false,
  kazooPlatformUrl = '',
  kazooLogoutUrl = '',
}) => {
  // @ts-expect-error TS(2339): Property 'propTypes' does not exist on type '({ sh... Remove this comment to see the full error message
  Sidebar.propTypes = {
    showSurveys: propTypes.bool,
    showConversations: propTypes.bool,
    showActionPlanning: propTypes.bool,
    showEmployees: propTypes.bool,
    showUsers: propTypes.bool,
    showMailingLists: propTypes.bool,
    showSettings: propTypes.bool,
    showAdmin: propTypes.bool,
    showTrends: propTypes.bool,
  };

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const styles = useStyles();
  const navigate = useNavigate();

  // Trigger `logout()` action (which clears out a bunch of data from the local
  // memory state and browser storage) and only then, redirect to the `/login` page.
  const handleLogout = () => {
    // @ts-ignore @TODO Epic WT-21600
    dispatch(accountActions.logout()).then(() => {
      if (showLeaveSurveysAndInsights) {
        location.goto(kazooLogoutUrl);
      } else {
        navigate('/login');
      }
    });
  };

  const handleLeaveSurveysAndInsights = () => {
    location.goto(kazooPlatformUrl);
  };

  return (
    <>
      <header className={styles.header}>
        <Link to="/" className={styles.logoContainer} data-testid="nav-root">
          <icons.WorkTangoWideWhiteLogo className={styles.logo} />
        </Link>
      </header>

      <nav className={styles.linksNav}>
        <mui.List component="ul" className={styles.list}>
          {showLeaveSurveysAndInsights && (
            <div>
              <icons.ArrowBack className={styles.backArrow} />
              <NavItem
                onClick={handleLeaveSurveysAndInsights}
                label={t('nav.leave.surveys.and.insights')}
                data-testid="nav-leave-pulse"
              ></NavItem>
              <hr className={styles.divider} />
              <div className={styles.pulseHeader}>
                <p>{t('nav.sidebar.title.surveys.and.insights')}</p>
              </div>
            </div>
          )}
          <NavItem url="/surveys" label={t('sidebar.surveys')} data-testid="nav-surveys" visible={showSurveys} />
          <NavItem url="/trends" label={t('trends.title')} data-testid="nav-trends" visible={showTrends} />
          <NavItem
            url="/conversations"
            label={t('sidebar.inbox')}
            data-testid="nav-conversations"
            visible={showConversations}
          />
          <NavItem
            url="/action-planning"
            label={t('sidebar.action-planning')}
            data-testid="nav-action-planning"
            visible={showActionPlanning}
          />
          <NavItem url="/users" label={t('admin.users.header')} data-testid="nav-users" visible={showUsers} />
          <NavItem
            url="/employees"
            label={t('sidebar.admin.employees')}
            data-testid="nav-employees"
            visible={showEmployees}
          />
          <NavItem
            url="/mailing-lists"
            label={t('sidebar.mailing-lists')}
            data-testid="nav-mailing-lists"
            visible={showMailingLists}
          />
          <NavItem
            url="/learning-coach"
            label={t('admin.companyData.learning.coach')}
            data-testid="nav-learning-coach"
            visible={showLearningCoach}
          />
          <NavItem url="/settings" label={t('sidebar.admin.data')} data-testid="nav-config" visible={showSettings} />
          <NavItem url="/help" label={t('help.header')} data-testid="nav-help" />
          <NavItem url="/admin" label={t('admin.title')} data-testid="nav-switch" visible={showAdmin} />

          <NavItem onClick={handleLogout} label={t('header.logout')} data-testid="nav-logout" />
        </mui.List>
      </nav>

      <mui.Grid className={styles.extraNav}>
        <mui.List className={styles.list}>
          <mui.ListItem className={styles.extraListItem}>
            <a
              className={styles.extraListItemText}
              href="https://www.worktango.com/privacy"
              target="_blank"
              rel="noreferrer"
            >
              {t('privacy.policy')}
            </a>
          </mui.ListItem>
        </mui.List>
      </mui.Grid>
    </>
  );
};

export default Sidebar;
